import React from 'react';
import { Leaf, Recycle, Heart, Users } from 'lucide-react';

const Sustainability = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <h1 className="text-3xl font-medium tracking-wide mb-8">SUSTAINABILITY</h1>
      
      <div className="space-y-12">
        <div>
          <p className="text-gray-700 leading-relaxed mb-6">
            At Dïtris, we believe fashion should never come at the expense of our planet. We're committed to creating quality streetwear while minimizing our environmental impact and supporting ethical practices throughout our supply chain.
          </p>
        </div>

        {/* Sustainable Materials */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Leaf size={28} />
            <h2 className="text-xl font-medium">Sustainable Materials</h2>
          </div>
          <div className="space-y-3 text-gray-700">
            <p>
              We prioritize sustainable and organic materials in our collections. Our cotton is sourced from certified organic farms that use less water and avoid harmful pesticides.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2">
              <li>Organic cotton certified by GOTS (Global Organic Textile Standard)</li>
              <li>Recycled polyester made from post-consumer plastic bottles</li>
              <li>Low-impact dyes that reduce water pollution</li>
              <li>Sustainable packaging materials</li>
            </ul>
          </div>
        </div>

        {/* Circular Fashion */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Recycle size={28} />
            <h2 className="text-xl font-medium">Circular Fashion</h2>
          </div>
          <div className="space-y-3 text-gray-700">
            <p>
              We design our products to last, encouraging a culture of quality over quantity. Our garments are built to withstand years of wear, reducing the need for frequent replacements.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2">
              <li>Durable construction techniques for longevity</li>
              <li>Timeless designs that transcend seasonal trends</li>
              <li>Repair guides and tips to extend garment life</li>
              <li>Take-back program for recycling worn items (coming soon)</li>
            </ul>
          </div>
        </div>

        {/* Ethical Production */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Heart size={28} />
            <h2 className="text-xl font-medium">Ethical Production</h2>
          </div>
          <div className="space-y-3 text-gray-700">
            <p>
              We partner with factories that share our values and provide safe, fair working conditions. All our manufacturing partners are audited regularly for compliance with ethical labor standards.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2">
              <li>Fair wages and reasonable working hours</li>
              <li>Safe and healthy working environments</li>
              <li>No child or forced labor</li>
              <li>Freedom of association and collective bargaining</li>
            </ul>
          </div>
        </div>

        {/* Community Impact */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Users size={28} />
            <h2 className="text-xl font-medium">Community Impact</h2>
          </div>
          <div className="space-y-3 text-gray-700">
            <p>
              We believe in giving back to the communities that support us. A portion of our profits goes to environmental and social causes that align with our values.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2">
              <li>1% of sales donated to environmental organizations</li>
              <li>Support for local community programs</li>
              <li>Partnerships with youth education initiatives</li>
              <li>Annual sustainability impact reports</li>
            </ul>
          </div>
        </div>

        {/* Our Goals */}
        <div className="bg-gray-50 p-6 rounded">
          <h2 className="text-xl font-medium mb-4">Our 2025-2030 Goals</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Achieve 100% sustainable materials across all products by 2027</li>
            <li>• Reduce carbon emissions by 50% by 2028</li>
            <li>• Eliminate single-use plastics from packaging by 2026</li>
            <li>• Launch garment recycling program in 2025</li>
            <li>• Achieve carbon neutrality by 2030</li>
          </ul>
        </div>

        {/* Transparency */}
        <div>
          <h2 className="text-xl font-medium mb-4">Transparency</h2>
          <p className="text-gray-700 mb-4">
            We believe in being open about our practices. While we're proud of the progress we've made, we know there's always more work to do.
          </p>
          <p className="text-gray-700">
            For detailed information about our sustainability initiatives, factory locations, and annual impact reports, please contact us at sustainability@ditris.com.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sustainability;